import React, { useMemo } from 'react';

/**
 * UINCDLetterHead
 * - Uses company props for name, address, contacts, and logo
 * - Inserts hard line breaks at specific character counts so very long names/addresses
 *   wrap EXACTLY at the template's length points (editable below)
 * - Produces valid, semantic HTML
 */

const NAME_BREAKS = [22, 24, 17];      // ← adjust to match your template line lengths
const ADDRESS_BREAKS = [24, 34, 36];   // ← adjust to match your template line lengths

/** Split a string into lines at precise character counts (without breaking the characters order). */
const splitAtPoints = (text = '', points = []) => {
  const s = String(text);
  if (!s) return [];
  const lines = [];
  let start = 0;
  for (let i = 0; i < points.length && start < s.length; i++) {
    const end = Math.min(start + points[i], s.length);
    lines.push(s.slice(start, end));
    start = end;
  }
  if (start < s.length) lines.push(s.slice(start));
  return lines.filter(Boolean);
};

const Lines = ({ lines }) => (
  <>
    {lines.map((ln, i) => (
      <span key={i}>
        {ln}
        {i < lines.length - 1 ? <br /> : null}
      </span>
    ))}
  </>
);

const cellBase = {
  padding: '6px 8px',
  verticalAlign: 'top',
  fontSize: '13px',
  fontFamily: 'Arial, Helvetica, sans-serif',
  color: '#000000',
  lineHeight: 1.4,
};

const UINCDLetterHead = ({ company = {}, title }) => {
  const {
    client_name,
    address,
    website,
    email_address,
    telephone,
    badge,
  } = company || {};

  const nameLines = useMemo(() => splitAtPoints(client_name, NAME_BREAKS), [client_name]);
  const addressLines = useMemo(() => splitAtPoints(address, ADDRESS_BREAKS), [address]);

  return (
    <div className="content-wrapper d-flex" style={{ width: '100%' }}>
      <table
        width="100%"
        style={{
          borderCollapse: 'collapse',
          borderBottom: '1px solid #000000',
          marginBottom: 8,
        }}
        aria-label="Letterhead"
        className='no-table'
      >
        <tbody>
          <tr>
            {/* Left: Company Name (hard-broken at template points) */}
            <td style={{ ...cellBase, width: '35%' }}>
              {nameLines.length ? <Lines lines={nameLines} /> : '—'}
            </td>

            {/* Middle: Logo (centered) */}
            <td style={{ ...cellBase, width: '30%', textAlign: 'center' }}>
              {badge ? (
                <img
                  src={badge}
                  alt={`${client_name || 'Company'} logo`}
                  style={{ maxHeight: 70, maxWidth: '100%', objectFit: 'contain' }}
                />
              ) : (
                <span className="text-muted small">No Logo</span>
              )}
            </td>

            {/* Right: Address & Contacts (address hard-broken at template points) */}
            <td style={{ ...cellBase, width: '35%', textAlign: 'right' }}>
              {addressLines.length ? <Lines lines={addressLines} /> : null}
              {(website || email_address || telephone) && (
                <>
                  <br />
                  {website && (
                    <span title="Website" style={{ whiteSpace: 'nowrap' }}>
                      &#x1F310;&nbsp;{website}
                    </span>
                  )}
                  {website && (email_address || telephone) ? <span>&nbsp;&nbsp;&nbsp;</span> : null}
                  {email_address && (
                    <span title="Email" style={{ whiteSpace: 'nowrap' }}>
                      &#x2709;&nbsp;{email_address}
                    </span>
                  )}
                 
                </>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Title below the rule */}
      {title && (
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            fontSize: '12px',
            marginTop: 5,
            marginBottom: 20,
            color: '#000000',
            fontWeight: 'bold',
          }}
        >
          {title}
        </div>
      )}
    </div>
  );
};

export default UINCDLetterHead;
